import { config } from '../../loader/core';
import $ from 'jquery';
import { read as readCookie} from './cookie';
import { data, jsonData } from './common';

var DEFAULT_LOCALE = 'en';

/**
 * An instance of this class is the view of i18n messages for a given HTML markup. This object holds the
 * actual locale, the i18n overrides and a prefix for the keys.
 *
 * The locale will be looked up in order:
 * 1. From the markup's "data-locale" attribute.
 * 2. From the "locale" global config key.
 * 3. From a cookie, with its name defined by the "cookie.locale" global config key.
 * 4. From attribute lang of html tag of the current page.
 * 5. A locale of "en" will be used.
 *
 * i18n overrides are defined by the markup in the data-i18n attribute.
 *
 * Prefix for the i18n keys is given in the params.keyPrefix parameter.
 *
 * @param {object} This parameter object that is a hash object with the below optional attributes:
 * - $markup: The HTML markup of a widget from which these i18n related data-* attributes are taken:
 *     -- data-i18n: A JSON string with a hash object of i18n key-value overrides,
 *     -- data-locale: Override of the locale.
 * - keyPrefix: The i18n key prefix for the given keys.
 */
export function I18nMessages(params) {
    var cookieName,
        locale = data(params.$markup, 'locale') || config('locale');

    if (!locale) {
        cookieName = config('cookie.locale');
        locale = readCookie(cookieName);
    }
    if (!locale) {
        locale = $('html').attr('lang'); // get locale from lang attribute of HTML tag of current page
    }

    this.locale = locale || DEFAULT_LOCALE;
    this.localeParts = getLocaleParts(this.locale);
    this.overrides = jsonData(params.$markup, 'i18n');
    this.keyPrefix = params.keyPrefix;
}

$.extend(I18nMessages.prototype, {
    /**
     * Resolves an i18n key using the below fallback chain until a defined value is found:
     * - First look up the key in the i18n overrides.
     * - Then resolve the key for the actual locale, then for the locale with one less part at the end, then
     *   two less parts, etc. finally with only the first part. Separator for locale parts are - and _, and
     *   parts are lowercased.
     * - Then take the value for the default locale of "en".
     *
     * @param {string} The i18n key without key prefixes.
     * @return {string} The resolved value.
     */
    get: function (key) {
        return resolveByOverride(key, this)
                || resolveByLocale(key, this)
                || resolveByDefaultLocale(key, this);
    }
});

function resolveByOverride(key, self) {
    return self.overrides ? self.overrides[key] : null;
}

function resolveByLocale(key, self) {
    var resolved,
        i,
        providedLocaleParts = self.localeParts,
        fallbackLocaleParts;
    // go through the locale fallback chain
    for (i = providedLocaleParts.length; i >= 1; i--) {
        fallbackLocaleParts = providedLocaleParts.slice(0, i);
        resolved = resolveKey(fallbackLocaleParts, self.keyPrefix, key);
        if (resolved) {
            break;
        }
    }
    return resolved;
}

function resolveByDefaultLocale(key, self) {
    return resolveKey(DEFAULT_LOCALE, self.keyPrefix, key);
}

/**
 * Split the given string by _ and - characters and lowercase each part.
 */
function getLocaleParts(locale) {
    var localeParts = locale.split(/[-_]/);
    localeParts = $.map(localeParts, function (el) {
        return el.toLowerCase();
    });
    return localeParts;
}

/**
 * Resolves an i18n key for a given locale.
 * @param {(string[]|string)} Array of locale parts or a string with only one part.
 * @param {string} Key prefix.
 * @param {string} Key without prefixes.
 */
function resolveKey(localeParts, keyPrefix, key) {
    var fullKey = [];
    fullKey.push('i18n');
    fullKey = fullKey.concat($.isArray(localeParts) ? localeParts.join('.') : localeParts);
    fullKey.push(keyPrefix || '');
    fullKey.push(key);
    return config(fullKey.join('.'));
}

/**
 * Returns a view of i18n messages for a given HTML markup. The returned view is a new object with a get(key)
 * function to resolve a value for a given key. For the actual resolution fallback chain, see JSDoc of constructor
 * of I18nMessages and the get(key) function.
 *
 * @param {object} Same hash object as in the constructor of I18nMessages.
 * @return {Object} The view of i18n messages for the given HTML markup. Always returns a new object.
 */
export default function i18n(params) {
    return new I18nMessages(params);
}



//////////////////
// WEBPACK FOOTER
// ./shared/modules/i18n.js
// module id = 7
// module chunks = main